import { CreditCard, Loader2 } from "lucide-react";
import { PaymentMethod } from "@/queries/payment-method/types";
import { Button } from "../ui/button-tw";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import useDisconnectPaymentMethod from "@/queries/payment-method/use-disconnect-payment-method";
import { toast } from "react-toastify";

type Props = {
  paymentMethod: PaymentMethod;
  onDisconnectSuccess?: () => void;
};

function parseCardJwt(linkSessionId?: string | null) {
  try {
    const payload = JSON.parse(atob(linkSessionId!.split(".")[1]));
    return {
      cardType: (payload.CardType as string) || "Credit Card",
      last4: ((payload.MaskedPan as string) || "****").slice(-4),
    };
  } catch {
    return { cardType: "Credit Card", last4: "****" };
  }
}

export function CreditCardPaymentMethodCard({ paymentMethod, onDisconnectSuccess }: Props) {
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const disconnectMutation = useDisconnectPaymentMethod();

  const { cardType, last4 } = parseCardJwt(
    paymentMethod.payment_method_data?.link_session_id
  );

  const businessName = paymentMethod.account_business_name;

  return (
    <>
      <div className="flex overflow-hidden bg-white shadow-sm rounded-xl w-full max-w-[370px] h-72 border-[3px] border-[#1DB45A]">
        <div className="flex flex-col justify-between p-6 md:p-8 w-full">
          <div>
            <div className="flex items-start">
              <CreditCard className="w-10 h-10 text-gray-700" strokeWidth={1.5} />
            </div>
            <div className="mt-5 mb-2">
              <h3 className="truncate text-xl font-medium text-gray-900">
                {cardType}
              </h3>
              <p className="font-medium text-sm text-gray-700 mt-1">
                ****{last4}
              </p>
            </div>
            {businessName && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ring-gray-200 text-gray-700">
                  <svg
                    className="h-1.5 w-1.5 fill-yellow-500"
                    viewBox="0 0 6 6"
                    aria-hidden="true"
                  >
                    <circle cx={3} cy={3} r={3} />
                  </svg>
                  {businessName}
                </span>
              </div>
            )}
          </div>
          <div>
            <Button
              variant="default"
              onClick={() => setConfirmRemoveOpen(true)}
            >
              Remove
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
        {/*@ts-ignore */}
        <AlertDialogContent>
          {/*@ts-ignore */}
          <AlertDialogHeader>
            {/*@ts-ignore */}
            <AlertDialogTitle>Remove Card?</AlertDialogTitle>
            {/*@ts-ignore */}
            <AlertDialogDescription>
              Are you sure you want to remove {cardType} ending in {last4}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/*@ts-ignore */}
          <AlertDialogFooter>
            {/*@ts-ignore */}
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={() => {
                disconnectMutation.mutate(
                  {
                    userId: paymentMethod.user_id,
                    type: paymentMethod.payment_method_type,
                    paymentMethodId: paymentMethod.payment_method_id,
                  },
                  {
                    onSettled: () => setConfirmRemoveOpen(false),
                    onSuccess: () => {
                      toast.success("Credit card removed successfully.");
                      onDisconnectSuccess?.();
                    },
                  }
                );
              }}
            >
              {disconnectMutation.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Remove"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
